import { useEffect, useContext } from 'react';

import useScreenCategory from '@hooks/use-screen-category';
import useNode from '@hooks/use-node';

import { layoutContext } from '@context/layout';

/**
 * This should only be used for components that are not in the document flow AND aren't
 * displayed initially. All elements in flow that are affected by the navbar should be
 * placed inside the content element of the layout component (which is already layout
 * aware for you). All elements that are SSR need to have CSS to handle their initial
 * styling as no effects will be run on the server.
 * @param show Notify the hook when the element to modify is in the DOM and can be modified
 */
export default function useLayoutOffset<T extends HTMLElement>(show = false): React.RefCallback<T> {
  const [elementToModify, elementToModifyRef] = useNode<T>();
  const { state: navState } = useContext(layoutContext);
  const [isSmall] = useScreenCategory();

  const isCollapsed = !!navState.collapsed;

  useEffect(() => {
    setTimeout(() => {
      if (!elementToModify) return;
      else {
        if (isSmall) elementToModify.style.setProperty('--nav-offset', '0');
        if (!isSmall && isCollapsed) elementToModify.style.setProperty('--nav-offset', '80px');
        if (!isSmall && !isCollapsed) elementToModify.style.setProperty('--nav-offset', '220px');
      }
    }, 0);
  }, [elementToModify, show, isCollapsed, isSmall]);

  return elementToModifyRef;
}
